// src/services/reports.js - Report API Service
import api from './api';

/**
 * Get modality-specific report template
 */
export const getReportTemplate = async (modality) => {
  try {
    const response = await api.get(`/reports/templates/${modality}`);
    return response.data;
  } catch (error) {
    // Backend now returns default template instead of 404, but handle errors just in case
    if (error?.response?.status === 404) {
      console.warn(`No report template found for modality "${modality}"`);
      return null;
    }
    console.error('Get report template error:', error);
    throw error;
  }
};

/**
 * Self-report (clinician)
 */
export const createSelfReport = async (studyId, reportData) => {
  try {
    const response = await api.post(`/reports/self/${studyId}`, reportData);
    return response.data;
  } catch (error) {
    console.error('Create self-report error:', error);
    throw error;
  }
};

export const getSelfReport = async (studyId) => {
  try {
    const response = await api.get(`/reports/self/${studyId}`);
    return response.data;
  } catch (error) {
    if (error?.response?.status === 404) {
      const code = error?.response?.data?.code;
      const msg = error?.response?.data?.error || '';
      if (code === 'STUDY_NOT_FOUND' || msg.includes('Study not found')) {
        throw error;
      }
      return null;
    }
    console.error('Get self-report error:', error);
    throw error;
  }
};

/** PACS study id for report APIs (scan-board rows may use order _id). */
export const resolveReportStudyId = (study) =>
  study?.pacsStudyId || study?.reportStudyId || study?.studyId || study?._id;

export const updateSelfReport = async (reportId, reportData) => {
  try {
    const response = await api.patch(`/reports/self/${reportId}`, reportData);
    return response.data;
  } catch (error) {
    console.error('Update self-report error:', error);
    throw error;
  }
};

export const approveSelfReport = async (reportId) => {
  try {
    const response = await api.post(`/reports/self/${reportId}/approve`);
    return response.data;
  } catch (error) {
    console.error('Approve self-report error:', error);
    throw error;
  }
};

/**
 * Dycovue report (radiologist)
 */
export const createDycovueReport = async (studyId, reportData) => {
  try {
    const response = await api.post(`/reports/dycovue/${studyId}`, reportData);
    return response.data;
  } catch (error) {
    console.error('Create Dycovue report error:', error);
    throw error;
  }
};

export const getDycovueReport = async (studyId) => {
  try {
    const response = await api.get(`/reports/dycovue/${studyId}`);
    return response.data;
  } catch (error) {
    // If report doesn't exist yet (404), return null instead of throwing
    // This is expected behavior when a report hasn't been created yet
    if (error?.response?.status === 404) {
      // Suppress console error for expected 404s
      return null;
    }
    // Only log unexpected errors
    if (error?.response?.status !== 404) {
      console.error('Get Dycovue report error:', error);
    }
    throw error;
  }
};

export const updateDycovueReport = async (reportId, reportData) => {
  try {
    const response = await api.patch(`/reports/dycovue/${reportId}`, reportData);
    return response.data;
  } catch (error) {
    console.error('Update Dycovue report error:', error);
    throw error;
  }
};

export const approveDycovueReport = async (reportId) => {
  try {
    const response = await api.post(`/reports/dycovue/${reportId}/approve`);
    return response.data;
  } catch (error) {
    console.error('Approve Dycovue report error:', error);
    throw error;
  }
};

export const submitDycovuePreliminary = async (reportId) => {
  try {
    const response = await api.post(`/reports/dycovue/${reportId}/submit-preliminary`);
    return response.data;
  } catch (error) {
    console.error('Submit preliminary report error:', error);
    throw error;
  }
};

export const qaRejectDycovueReport = async (reportId, comments) => {
  try {
    const response = await api.post(`/reports/dycovue/${reportId}/qa-reject`, { comments });
    return response.data;
  } catch (error) {
    console.error('QA reject report error:', error);
    throw error;
  }
};

export const getQaQueue = async () => {
  try {
    const response = await api.get('/reports/dycovue/qa-queue');
    return response.data;
  } catch (error) {
    console.error('Get QA queue error:', error);
    throw error;
  }
};

/**
 * Shared report helpers
 */
export const flagCriticalFinding = async (reportId, description) => {
  try {
    const response = await api.post(`/reports/${reportId}/flag-critical`, { description });
    return response.data;
  } catch (error) {
    console.error('Flag critical finding error:', error);
    throw error;
  }
};

export const autoSaveReport = async (reportId, reportData) => {
  try {
    const response = await api.post(`/reports/${reportId}/auto-save`, reportData);
    return response.data;
  } catch (error) {
    console.error('Auto-save report error:', error);
    throw error;
  }
};

export const getBothReports = async (studyId) => {
  try {
    const response = await api.get(`/reports/study/${studyId}/both`);
    return response.data;
  } catch (error) {
    console.error('Get both reports error:', error);
    throw error;
  }
};

export const downloadReportPDF = async (reportId, options = {}) => {
  try {
    const { language = 'en', simplify = false } = options;
    
    // Build query parameters
    const params = new URLSearchParams();
    if (language !== 'en') {
      params.append('language', language);
    }
    if (simplify) {
      params.append('simplify', 'true');
    }
    
    const queryString = params.toString();
    const url = `/reports/${reportId}/pdf${queryString ? `?${queryString}` : ''}`;
    
    const response = await api.get(url, {
      responseType: 'blob'
    });

    // Generate filename with language/simplify suffix if applicable
    let filename = `report-${reportId}`;
    if (language !== 'en') {
      filename += `-${language}`;
    }
    if (simplify) {
      filename += '-simple';
    }
    filename += '.pdf';

    const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const link = document.createElement('a');
    link.href = blobUrl;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download report PDF error:', error);
    throw error;
  }
};

/**
 * Clean isolated client-side print for medical reports via dedicated iframe
 * Completely decouples the report from the dashboard/worklist background DOM.
 */
export const printClinicalReportViaIframe = (report = {}, study = {}, type = 'report') => {
  const patientName = study?.patientName || report?.patientName || 'NAME NONE';
  const patientId = study?.patientID || study?.patientId || report?.patientId || 'N/A';
  const patientGender = study?.patientSex || study?.patientGender || report?.patientGender || 'Unknown';
  const patientAge = study?.patientAge || report?.patientAge || (study?.patientBirthDate ? `${new Date().getFullYear() - new Date(study.patientBirthDate).getFullYear()} Y` : 'N/A');
  const studyDate = study?.studyDate ? new Date(study.studyDate).toLocaleDateString() : (report?.createdAt ? new Date(report.createdAt).toLocaleDateString() : new Date().toLocaleDateString());
  const modality = report?.modality || study?.modality || 'CT';
  const accession = study?.accessionNumber || report?.accessionNumber || 'N/A';
  const referringPhysician = study?.referringPhysician || report?.referringPhysician || 'N/A';
  const studyDesc = study?.studyDescription || report?.studyDescription || 'Diagnostic Examination';
  
  const doctorName = report?.approvedBy 
    ? `${report.approvedBy.firstName || ''} ${report.approvedBy.lastName || ''}`.trim()
    : report?.createdBy 
      ? `${report.createdBy.firstName || ''} ${report.createdBy.lastName || ''}`.trim()
      : 'Staff Radiologist';

  const approvalDate = report?.approvedAt ? new Date(report.approvedAt).toLocaleString() : new Date().toLocaleString();
  const title = type === 'self-report' ? 'CLINICAL SELF-REPORT' : type === 'working-copy' ? 'ADDENDUM / REVISED REPORT' : 'DYCOVUE RADIOLOGY REPORT';

  const html = `
    <!DOCTYPE html>
    <html>
    <head>
      <meta charset="utf-8">
      <title>Radiology Report - ${patientName}</title>
      <style>
        @page {
          size: A4 portrait;
          margin: 15mm;
        }
        * {
          box-sizing: border-box;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
          color: #0F172A;
          background: #FFFFFF !important;
          margin: 0;
          padding: 8px;
          font-size: 10pt;
          line-height: 1.45;
        }
        .header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          border-bottom: 2px solid #2563EB;
          padding-bottom: 10px;
          margin-bottom: 12px;
        }
        .hospital-title {
          font-size: 15pt;
          font-weight: 800;
          color: #1E3A8A;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }
        .hospital-sub {
          font-size: 8.5pt;
          color: #64748B;
          margin-top: 2px;
        }
        .report-badge {
          text-align: right;
        }
        .badge-pill {
          display: inline-block;
          background: #EFF6FF;
          border: 1px solid #3B82F6;
          color: #1D4ED8;
          font-weight: 700;
          font-size: 8.5pt;
          padding: 3px 8px;
          border-radius: 4px;
          margin-bottom: 4px;
        }
        .status-pill {
          display: inline-block;
          background: #ECFDF5;
          border: 1px solid #10B981;
          color: #047857;
          font-weight: 700;
          font-size: 8pt;
          padding: 2px 7px;
          border-radius: 4px;
        }
        .demographics-table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 14px;
          background: #F8FAFC;
          border: 1px solid #CBD5E1;
          border-radius: 6px;
          overflow: hidden;
        }
        .demographics-table td {
          padding: 5px 8px;
          font-size: 9pt;
          border: 1px solid #E2E8F0;
          vertical-align: top;
        }
        .label {
          color: #64748B;
          font-weight: 600;
          font-size: 7.5pt;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          margin-bottom: 1px;
        }
        .val {
          color: #0F172A;
          font-weight: 700;
        }
        .section {
          margin-bottom: 10px;
          page-break-inside: avoid;
        }
        .section-header {
          font-size: 9pt;
          font-weight: 800;
          color: #1E3A8A;
          text-transform: uppercase;
          letter-spacing: 0.4px;
          border-bottom: 1px solid #E2E8F0;
          padding-bottom: 2px;
          margin-bottom: 4px;
        }
        .section-body {
          font-size: 9.5pt;
          color: #1E293B;
          white-space: pre-wrap;
          line-height: 1.45;
        }
        .impression-box {
          background: #F0FDF4;
          border: 1px solid #86EFAC;
          border-left: 4px solid #16A34A;
          padding: 7px 10px;
          border-radius: 4px;
          margin-bottom: 10px;
          page-break-inside: avoid;
        }
        .impression-box .section-header {
          color: #166534;
          border-bottom: none;
          padding-bottom: 0;
          margin-bottom: 3px;
        }
        .critical-box {
          background: #FEF2F2;
          border: 1px solid #EF4444;
          padding: 7px 10px;
          border-radius: 4px;
          margin-bottom: 10px;
          color: #991B1B;
          font-size: 9.5pt;
          page-break-inside: avoid;
        }
        .footer-sig {
          margin-top: 20px;
          padding-top: 10px;
          border-top: 1px solid #E2E8F0;
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          page-break-inside: avoid;
        }
        .sig-block {
          font-size: 9pt;
        }
        .sig-name {
          font-weight: 700;
          color: #0F172A;
          font-size: 10pt;
        }
        .sig-details {
          color: #64748B;
          font-size: 8pt;
          margin-top: 1px;
        }
        .disclaimer {
          font-size: 7.5pt;
          color: #94A3B8;
          text-align: right;
          max-width: 250px;
        }
      </style>
    </head>
    <body>
      <div class="header">
        <div>
          <div class="hospital-title">DycoVue PACS & Diagnostic Radiology</div>
          <div class="hospital-sub">Diagnostic Imaging & Clinical Radiology Services</div>
        </div>
        <div class="report-badge">
          <div class="badge-pill">${title}</div><br/>
          <span class="status-pill">APPROVED / FINAL</span>
        </div>
      </div>

      <table class="demographics-table">
        <tr>
          <td width="30%">
            <div class="label">Patient Name</div>
            <div class="val">${patientName}</div>
          </td>
          <td width="22%">
            <div class="label">Patient ID / MRN</div>
            <div class="val">${patientId}</div>
          </td>
          <td width="22%">
            <div class="label">Gender / Age</div>
            <div class="val">${patientGender} / ${patientAge}</div>
          </td>
          <td width="26%">
            <div class="label">Study Date</div>
            <div class="val">${studyDate}</div>
          </td>
        </tr>
        <tr>
          <td>
            <div class="label">Study Description</div>
            <div class="val">${studyDesc}</div>
          </td>
          <td>
            <div class="label">Modality</div>
            <div class="val">${modality}</div>
          </td>
          <td>
            <div class="label">Accession #</div>
            <div class="val">${accession}</div>
          </td>
          <td>
            <div class="label">Referring Clinician</div>
            <div class="val">${referringPhysician}</div>
          </td>
        </tr>
      </table>

      ${report?.criticalFindings?.present ? `
        <div class="critical-box">
          <strong>CRITICAL FINDING:</strong> ${report.criticalFindings.description || 'Actionable findings reported.'}
        </div>
      ` : ''}

      ${report?.clinicalHistory ? `
        <div class="section">
          <div class="section-header">Clinical History / Indication</div>
          <div class="section-body">${report.clinicalHistory}</div>
        </div>
      ` : ''}

      ${report?.technique ? `
        <div class="section">
          <div class="section-header">Technique</div>
          <div class="section-body">${report.technique}</div>
        </div>
      ` : ''}

      ${report?.comparison ? `
        <div class="section">
          <div class="section-header">Comparison</div>
          <div class="section-body">${report.comparison}</div>
        </div>
      ` : ''}

      <div class="section">
        <div class="section-header">Findings</div>
        <div class="section-body">${report?.findings || 'No findings recorded.'}</div>
      </div>

      <div class="impression-box">
        <div class="section-header">Impression</div>
        <div class="section-body" style="font-weight: 600;">${report?.impression || 'No impression recorded.'}</div>
      </div>

      ${report?.recommendations ? `
        <div class="section">
          <div class="section-header">Recommendations</div>
          <div class="section-body">${report.recommendations}</div>
        </div>
      ` : ''}

      ${report?.modalityFields && Object.keys(report.modalityFields).length > 0 ? `
        <div class="section">
          <div class="section-header">${modality} Specific Findings</div>
          <div class="section-body">
            ${Object.entries(report.modalityFields).map(([k, v]) => `<strong>${k}:</strong> ${typeof v === 'object' ? JSON.stringify(v) : v}`).join('<br/>')}
          </div>
        </div>
      ` : ''}

      <div class="footer-sig">
        <div class="sig-block">
          <div class="sig-name">Electronically Signed: ${doctorName}</div>
          <div class="sig-details">Approved on: ${approvalDate} • Verified Digital Record</div>
          <div class="sig-details">Report ID: ${report?._id || 'N/A'}</div>
        </div>
        <div class="disclaimer">
          CONFIDENTIAL MEDICAL RECORD<br/>
          This document contains protected health information (PHI). Generated via DycoVue PACS.
        </div>
      </div>
    </body>
    </html>
  `;

  const printFrame = document.createElement('iframe');
  printFrame.style.position = 'fixed';
  printFrame.style.right = '0';
  printFrame.style.bottom = '0';
  printFrame.style.width = '0';
  printFrame.style.height = '0';
  printFrame.style.border = '0';
  document.body.appendChild(printFrame);

  const doc = printFrame.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  setTimeout(() => {
    try {
      printFrame.contentWindow.focus();
      printFrame.contentWindow.print();
    } catch (e) {
      console.error('Print iframe error:', e);
    }
    setTimeout(() => {
      try {
        document.body.removeChild(printFrame);
      } catch (_) {}
    }, 60000);
  }, 250);
};

/**
 * Print a report PDF via browser print dialog.
 * If server PDF is unavailable, falls back cleanly to client-side isolated iframe print.
 */
export const printReportPDF = async (reportId, options = {}) => {
  try {
    const { language = 'en', simplify = false } = options;
    const params = new URLSearchParams();
    if (language !== 'en') params.append('language', language);
    if (simplify) params.append('simplify', 'true');
    const queryString = params.toString();
    const url = `/reports/${reportId}/pdf${queryString ? `?${queryString}` : ''}`;

    const response = await api.get(url, {
      responseType: 'blob'
    });

    const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'fixed';
    printFrame.style.right = '0';
    printFrame.style.bottom = '0';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    printFrame.src = blobUrl;
    document.body.appendChild(printFrame);

    printFrame.onload = () => {
      try {
        printFrame.contentWindow.focus();
        printFrame.contentWindow.print();
      } catch (e) {
        window.open(blobUrl, '_blank');
      }
      setTimeout(() => {
        try {
          document.body.removeChild(printFrame);
          window.URL.revokeObjectURL(blobUrl);
        } catch (_) {}
      }, 60000);
    };
  } catch (error) {
    console.warn('Backend PDF generation unavailable, using isolated client-side report print:', error);
    if (options.report || options.study) {
      printClinicalReportViaIframe(options.report, options.study, options.type);
    } else {
      // Attempt to find report dialog or fetch report
      try {
        const reportRes = await api.get(`/reports/${reportId}`);
        const fetchedReport = reportRes?.data?.report || reportRes?.data;
        printClinicalReportViaIframe(fetchedReport, fetchedReport?.study, 'report');
      } catch (_) {
        printClinicalReportViaIframe({ _id: reportId }, {}, 'report');
      }
    }
  }
};

/**
 * Update a report's working copy (editable version)
 * reportType must be 'self-report' or 'dycovue-report'
 */
export const updateReportWorkingCopy = async (reportId, reportType, reportData) => {
  try {
    const endpoint = reportType === 'self-report' ? 'self' : 'dycovue';
    const response = await api.patch(`/reports/${endpoint}/${reportId}`, reportData);
    return response.data;
  } catch (error) {
    console.error('Update working copy error:', error);
    throw error;
  }
};

/**
 * Hospital / Clinician approves in-house report ("all is calm")
 */
export const facilityApproveReport = async (reportId) => {
  try {
    const response = await api.post(`/reports/${reportId}/facility-approve`);
    return response.data;
  } catch (error) {
    console.error('Facility approve report error:', error);
    throw error;
  }
};

/**
 * Hospital / Clinician flags concerns with in-house report
 */
export const facilityFlagReport = async (reportId, concerns) => {
  try {
    const response = await api.post(`/reports/${reportId}/facility-flag`, { concerns });
    return response.data;
  } catch (error) {
    console.error('Facility flag report error:', error);
    throw error;
  }
};

/**
 * In-house radiologist (or reviewer) replies to flagged concerns on specific report
 */
export const facilityReplyReport = async (reportId, reply) => {
  try {
    const response = await api.post(`/reports/${reportId}/facility-reply`, { reply });
    return response.data;
  } catch (error) {
    console.error('Facility reply report error:', error);
    throw error;
  }
};